import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Input, InputNumber, Select, Button, Card, Typography, message, Alert, Descriptions, Table, Tag, Segmented } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { checkPayoutEligibility, createPayout, getBankAccounts, getPayoutEwallet, getPersonalPayoutAccounts } from '../../services/payout'
import type { CreatePayoutMethod, PayoutEligibilityResponse, PayoutEwallet } from '../../services/payout'
import type { BankAccount, PersonalPayoutAccount, PayoutCapability } from '../../types'
import { calculatePayoutFee, calculatePayoutAmountFromNet, compareDecimalAmounts, formatDecimalAmount, formatDecimalPercent, isZeroDecimal, isPositiveMultipleOf } from '../../utils/decimalAmount'

const { Title, Text } = Typography

type PayoutRateRow = {
  key: string
  channel: string
  fee: string
  minimum: string
}

type UsdtServiceType = 'ORDINARY' | 'URGENT'

interface PayoutFormValues {
  amount: string
  netAmount?: string
  method: CreatePayoutMethod
  bankAccountId?: string
  personalPayoutAccountId?: string
  totpCode?: string
}

const PAYOUT_CAPABILITY_ROWS: Array<{ capability: PayoutCapability; channel: string }> = [
  { capability: 'OVERSEAS_BUSINESS_ACCOUNT', channel: '海外企業賬戶' },
  { capability: 'OVERSEAS_EWALLET_ORDINARY', channel: '海外電子錢包（普通提現）' },
  { capability: 'OVERSEAS_EWALLET_URGENT', channel: '海外電子錢包（緊急提現）' },
  { capability: 'PERSONAL_EWALLET', channel: '個人電子錢包' },
  { capability: 'PERSONAL_BANK', channel: '個人銀行賬戶' },
]

const formatProcessingDate = (timestamp: number): string => new Date(timestamp).toLocaleDateString('zh-CN', {
  timeZone: 'Asia/Shanghai',
})

const formatMoneyInput = (value: string | undefined, info: { userTyping: boolean; input: string }): string => {
  if (info.userTyping) return info.input
  return value ? formatDecimalAmount(value) : ''
}

const parseMoneyInput = (value: string | undefined): string => (value ?? '').replace(/,/g, '')

function isCertificationApproved(method: CreatePayoutMethod, eligibility: PayoutEligibilityResponse): boolean {
  if (method === 'BANK_BUSINESS') return eligibility.businessKybApproved
  if (method === 'PERSONAL_BANK' || method === 'PERSONAL_EWALLET') return eligibility.personalKycApproved
  return eligibility.personalKycApproved || eligibility.businessKybApproved
}

function certificationRequirement(method: CreatePayoutMethod): string {
  if (method === 'BANK_BUSINESS') return '需先通過企業 KYB'
  if (method === 'PERSONAL_BANK' || method === 'PERSONAL_EWALLET') return '需先通過個人 KYC'
  return '需先通過個人 KYC 或企業 KYB'
}

export default function PayoutCreate() {
  const [eligibility, setEligibility] = useState<PayoutEligibilityResponse | null>(null)
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([])
  const [personalAccounts, setPersonalAccounts] = useState<PersonalPayoutAccount[]>([])
  const [payoutEwallet, setPayoutEwallet] = useState<PayoutEwallet | null>(null)
  const [payoutEwalletLoading, setPayoutEwalletLoading] = useState(true)
  const [payoutEwalletLoadError, setPayoutEwalletLoadError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [criticalLoadError, setCriticalLoadError] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [amount, setAmount] = useState<string | null>(null)
  const [method, setMethod] = useState<CreatePayoutMethod | undefined>()
  const [selectedUsdtServiceType, setSelectedUsdtServiceType] = useState<UsdtServiceType>()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const businessKybApproved = eligibility?.businessKybApproved ?? false
  const personalKycApproved = eligibility?.personalKycApproved ?? false
  const capabilities = new Set(eligibility?.enabledCapabilities ?? [])
  const methodOptions = [
    ...(capabilities.has('OVERSEAS_BUSINESS_ACCOUNT') ? [{
      value: 'BANK_BUSINESS' as const,
      label: '海外企業賬戶',
      disabled: !businessKybApproved,
      title: businessKybApproved ? undefined : certificationRequirement('BANK_BUSINESS'),
    }] : []),
    ...(capabilities.has('OVERSEAS_EWALLET_ORDINARY') || capabilities.has('OVERSEAS_EWALLET_URGENT')
      ? [{
          value: 'USDT' as const,
          label: '海外電子錢包',
          disabled: !personalKycApproved && !businessKybApproved,
          title: personalKycApproved || businessKybApproved ? undefined : certificationRequirement('USDT'),
        }]
      : []),
    ...(capabilities.has('PERSONAL_EWALLET') ? [{
      value: 'PERSONAL_EWALLET' as const,
      label: '個人電子錢包',
      disabled: !personalKycApproved,
      title: personalKycApproved ? undefined : certificationRequirement('PERSONAL_EWALLET'),
    }] : []),
    ...(capabilities.has('PERSONAL_BANK') ? [{
      value: 'PERSONAL_BANK' as const,
      label: '個人銀行賬戶',
      disabled: !personalKycApproved,
      title: personalKycApproved ? undefined : certificationRequirement('PERSONAL_BANK'),
    }] : []),
  ]
  const usdtServiceOptions = [
    ...(capabilities.has('OVERSEAS_EWALLET_ORDINARY') ? [{ label: '普通提現', value: 'ORDINARY' as const }] : []),
    ...(capabilities.has('OVERSEAS_EWALLET_URGENT') ? [{ label: '緊急提現', value: 'URGENT' as const }] : []),
  ]
  const usdtServiceType = selectedUsdtServiceType === 'ORDINARY' && capabilities.has('OVERSEAS_EWALLET_ORDINARY')
    ? selectedUsdtServiceType
    : selectedUsdtServiceType === 'URGENT' && capabilities.has('OVERSEAS_EWALLET_URGENT')
      ? selectedUsdtServiceType
      : capabilities.has('OVERSEAS_EWALLET_ORDINARY')
        ? 'ORDINARY'
        : capabilities.has('OVERSEAS_EWALLET_URGENT')
          ? 'URGENT'
          : undefined

  const loadPayoutEwallet = useCallback(async () => {
    setPayoutEwalletLoading(true)
    setPayoutEwalletLoadError(false)
    try {
      const response = await getPayoutEwallet()
      setPayoutEwallet(response.data ?? null)
    } catch {
      setPayoutEwalletLoadError(true)
    } finally {
      setPayoutEwalletLoading(false)
    }
  }, [])

  const loadCriticalData = useCallback(async () => {
    setLoading(true)
    setCriticalLoadError(false)
    setEligibility(null)
    try {
      const [eligibilityRes, accountsRes, personalAccountsRes] = await Promise.all([
        checkPayoutEligibility(),
        getBankAccounts(),
        getPersonalPayoutAccounts(),
      ])
      setEligibility(eligibilityRes.data)
      setBankAccounts(accountsRes.data)
      setPersonalAccounts(personalAccountsRes.data)
    } catch {
      setCriticalLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadCriticalData()
    void loadPayoutEwallet()
  }, [loadCriticalData, loadPayoutEwallet])

  const handleSubmit = async (values: PayoutFormValues) => {
    if (!eligibility || !isCertificationApproved(values.method, eligibility)) {
      message.error(certificationRequirement(values.method))
      return
    }
    if (values.method === 'USDT' && !payoutEwallet) {
      message.error('請先添加海外電子錢包地址')
      return
    }
    setSubmitting(true)
    try {
      await createPayout({
        amount: values.amount,
        method: values.method,
        bankAccountId: values.method === 'BANK_BUSINESS' ? values.bankAccountId : undefined,
        personalPayoutAccountId: values.method === 'PERSONAL_BANK' || values.method === 'PERSONAL_EWALLET' ? values.personalPayoutAccountId : undefined,
        usdtServiceType: values.method === 'USDT' ? usdtServiceType : undefined,
        totpCode: values.totpCode,
      })
      message.success('結款申請已提交')
      navigate('/dashboard/payouts')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '提交失敗')
    } finally {
      setSubmitting(false)
    }
  }

  const formatAmount = (amt: string | number) => formatDecimalAmount(String(amt))
  const formatMoney = (amt: string | number) => `$${formatDecimalAmount(String(amt))}`
  const formatFee = (rate: string, fixed?: string | null) => {
    const parts: string[] = []
    if (!isZeroDecimal(rate)) parts.push(formatDecimalPercent(rate))
    if (fixed != null && !isZeroDecimal(fixed)) parts.push(formatMoney(fixed))
    return parts.length > 0 ? parts.join(' + ') : formatMoney(0)
  }
  const payoutRateRows: PayoutRateRow[] = eligibility
    ? PAYOUT_CAPABILITY_ROWS
        .filter(({ capability }) => capabilities.has(capability))
        .map(({ capability, channel }) => ({
          key: capability,
          channel,
          fee: formatFee(eligibility.feeConfigs[capability].feeRate, eligibility.feeConfigs[capability].fixedFee),
          minimum: eligibility.minPayoutAmounts[capability],
        }))
    : []

  const payoutRateColumns: ColumnsType<PayoutRateRow> = [
    {
      title: '結款渠道',
      dataIndex: 'channel',
      key: 'channel',
    },
    {
      title: '費率',
      dataIndex: 'fee',
      key: 'fee',
      render: (fee: string) => <Tag color="geekblue">{fee}</Tag>,
    },
    {
      title: '最低結款金額',
      dataIndex: 'minimum',
      key: 'minimum',
      render: (minimum: string) => `${formatMoney(minimum)} USD`,
    },
  ]

  const selectedCapability: PayoutCapability | undefined = method === 'BANK_BUSINESS'
    ? 'OVERSEAS_BUSINESS_ACCOUNT'
    : method === 'USDT'
      ? usdtServiceType === 'URGENT'
        ? 'OVERSEAS_EWALLET_URGENT'
        : usdtServiceType === 'ORDINARY'
          ? 'OVERSEAS_EWALLET_ORDINARY'
          : undefined
      : method === 'PERSONAL_EWALLET'
        ? 'PERSONAL_EWALLET'
        : method === 'PERSONAL_BANK'
          ? 'PERSONAL_BANK'
          : undefined
  const selectedFeeConfig = eligibility && selectedCapability
    ? eligibility.feeConfigs[selectedCapability]
    : undefined
  const selectedMinimum = eligibility && selectedCapability
    ? eligibility.minPayoutAmounts[selectedCapability]
    : undefined

  useEffect(() => {
    if (form.getFieldValue('amount') != null) {
      void form.validateFields(['amount']).catch(() => undefined)
    }
  }, [form, selectedMinimum])

  const feeEstimate = amount && selectedFeeConfig
    ? calculatePayoutFee(amount, selectedFeeConfig.feeRate, selectedFeeConfig.fixedFee)
    : null

  const selectedFeeRate = selectedFeeConfig?.feeRate
  const selectedFixedFee = selectedFeeConfig?.fixedFee

  useEffect(() => {
    if (!method || selectedFeeRate == null || selectedFixedFee == null) return
    const currentAmount = form.getFieldValue('amount') as string | null | undefined
    form.setFieldValue('netAmount', currentAmount != null
      ? calculatePayoutFee(currentAmount, selectedFeeRate, selectedFixedFee).netAmount
      : null)
  }, [form, method, selectedFeeRate, selectedFixedFee])

  const handleAmountChange = (value: string | null) => {
    setAmount(value)
    if (method && selectedFeeConfig) {
      form.setFieldValue('netAmount', value == null ? null
        : calculatePayoutFee(value, selectedFeeConfig.feeRate, selectedFeeConfig.fixedFee).netAmount)
      if (value != null) void form.validateFields(['netAmount']).catch(() => undefined)
    }
  }

  const handleNetAmountChange = (value: string | null) => {
    const grossAmount = value == null || !selectedFeeConfig ? null
      : calculatePayoutAmountFromNet(value, selectedFeeConfig.feeRate, selectedFeeConfig.fixedFee)
    setAmount(grossAmount)
    form.setFieldValue('amount', grossAmount)
    if (grossAmount != null) void form.validateFields(['amount']).catch(() => undefined)
  }

  const selectedUsdtRate = selectedFeeConfig?.feeRate ?? '0'
  const selectedUsdtFixed = selectedFeeConfig?.fixedFee ?? '0'
  const selectedExpectedProcessingAt = eligibility
    ? usdtServiceType === 'URGENT'
      ? eligibility.usdtUrgentExpectedProcessingAt
      : eligibility.usdtOrdinaryExpectedProcessingAt
    : undefined

  if (loading) {
    return <Card loading style={{ maxWidth: 600 }} />
  }

  if (criticalLoadError || !eligibility) {
    return (
      <div>
        <Title level={4} style={{ marginBottom: 24 }}>發起結款</Title>
        <Alert
          type="error"
          showIcon
          message="結款資料載入失敗"
          description="無法載入結款資格或收款賬戶資料，請重新載入。"
          action={<Button onClick={() => void loadCriticalData()}>重新載入</Button>}
        />
      </div>
    )
  }

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>發起結款</Title>

      {!eligibility.eligible && (
        <Alert
          type="warning"
          message="結款暫時不可用"
          description={eligibility.reasons.join('；')}
          style={{ marginBottom: 24 }}
          showIcon
        />
      )}

      <Card style={{ maxWidth: 760, marginBottom: 24 }}>
        <Descriptions title="賬戶資訊" column={1}>
          <Descriptions.Item label="可用餘額">
            ${formatAmount(eligibility.availableBalance)}
          </Descriptions.Item>
          <Descriptions.Item label="最低結款金額">
            {selectedMinimum ? `${formatMoney(selectedMinimum)} USD` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="KYC 狀態">
            {personalKycApproved ? '已通過' : '未通過'}
          </Descriptions.Item>
          <Descriptions.Item label="KYB 狀態">
            {businessKybApproved ? '已通過' : '未通過'}
          </Descriptions.Item>
        </Descriptions>
        <Title level={5} style={{ marginTop: 24 }}>結款費率</Title>
        <Table
          size="small"
          pagination={false}
          columns={payoutRateColumns}
          dataSource={payoutRateRows}
          locale={{ emptyText: '暫無結款費率資料' }}
        />
      </Card>

      <Card style={{ maxWidth: 600 }}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="method"
            label="結款方式"
            rules={[{ required: true, message: '請選擇結款方式' }]}
          >
            <Select
              options={methodOptions}
              onChange={(val: CreatePayoutMethod) => {
                setMethod(val)
                form.setFieldValue('bankAccountId', undefined)
                form.setFieldValue('personalPayoutAccountId', undefined)
              }}
            />
          </Form.Item>
          {method === 'PERSONAL_BANK' && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="個人銀行提款每週五集中處理"
              description={
                <div>
                  <div>北京時間週五 16:00 前提交可於當日處理，16:00 起提交順延至下週五。扣除手續費後的到賬金額須為 500.00 USD 的正整數倍。</div>
                  {eligibility.personalBankExpectedProcessingAt && (
                    <Text strong>預計處理日期：{formatProcessingDate(eligibility.personalBankExpectedProcessingAt)}</Text>
                  )}
                </div>
              }
            />
          )}
          {method === 'USDT' ? (
            <>
              <Form.Item label="提現類型" required>
                <Segmented
                  block
                  value={usdtServiceType}
                  onChange={(value) => setSelectedUsdtServiceType(value as UsdtServiceType)}
                  options={usdtServiceOptions}
                />
              </Form.Item>
              <Alert
                type={usdtServiceType === 'URGENT' ? 'warning' : 'info'}
                showIcon
                style={{ marginBottom: 16 }}
                message={usdtServiceType === 'URGENT' ? '緊急提現' : '普通提現'}
                description={
                  <div>
                    <div>
                      {formatFee(selectedUsdtRate, selectedUsdtFixed)}
                    </div>
                    <div>
                      {usdtServiceType === 'URGENT'
                        ? '香港工作日優先處理，預計當日到賬；非香港工作日提交後 24 小時（D+1）內到賬。'
                        : '香港工作日提現，16:00 前提交預計下一香港工作日（T+1）到賬；16:00 起順延一個受理工作日。'}
                    </div>
                    {selectedExpectedProcessingAt && (
                      <Text strong>{usdtServiceType === 'URGENT' ? '預計到賬時間' : '預計到賬日期'}：{usdtServiceType === 'URGENT'
                        ? new Date(selectedExpectedProcessingAt).toLocaleString('zh-CN', { timeZone: 'Asia/Hong_Kong', hour12: false })
                        : formatProcessingDate(selectedExpectedProcessingAt)}</Text>
                    )}
                  </div>
                }
              />
              {payoutEwalletLoading ? (
              <Card loading size="small" style={{ marginBottom: 24 }} />
            ) : payoutEwalletLoadError ? (
              <Alert
                type="error"
                showIcon
                message="海外電子錢包地址載入失敗"
                action={<Button size="small" onClick={loadPayoutEwallet}>重新載入</Button>}
                style={{ marginBottom: 24 }}
              />
            ) : payoutEwallet ? (
              <Descriptions bordered size="small" column={1} style={{ marginBottom: 24 }}>
                <Descriptions.Item label="鏈">{payoutEwallet.chain}</Descriptions.Item>
                <Descriptions.Item label="錢包地址">
                  <Text copyable={{ text: payoutEwallet.address }}>{payoutEwallet.address}</Text>
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Alert
                type="warning"
                showIcon
                message="尚未添加海外電子錢包地址"
                action={<Button size="small" onClick={() => navigate('/dashboard/bank-accounts')}>前往添加</Button>}
                style={{ marginBottom: 24 }}
              />
            )
              }
            </>
          ) : method === 'PERSONAL_BANK' || method === 'PERSONAL_EWALLET' ? (
            <Form.Item
              name="personalPayoutAccountId"
              label="個人提款賬戶"
              rules={[{ required: true, message: '請選擇個人提款賬戶' }]}
            >
              <Select
                placeholder="選擇個人提款賬戶"
                options={personalAccounts
                  .filter((acc) => method === 'PERSONAL_BANK' ? acc.accountKind === 'BANK' : acc.accountKind === 'E_WALLET')
                  .map((acc) => ({
                    value: acc.id,
                    label: acc.accountKind === 'BANK'
                      ? `${acc.bankName || acc.paymentMethod} - ****${acc.accountNumberLast4 || ''}`
                      : `${acc.paymentMethod} - ${acc.fields?.[0]?.displayValue || acc.fields?.[0]?.value || acc.currency}`
                  }))}
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="bankAccountId"
              label="銀行賬戶"
              rules={[{ required: method === 'BANK_BUSINESS', message: '請選擇銀行賬戶' }]}
            >
              <Select
                placeholder="選擇銀行賬戶"
                options={bankAccounts.map((acc) => ({
                  value: acc.id,
                  label: `${acc.bankName} - ****${acc.accountNumberLast4}`
                }))}
              />
            </Form.Item>
          )}
          <Form.Item
            name="amount"
            label="結款金額"
            dependencies={['method']}
            rules={[
              { required: true, message: '請輸入結款金額' },
              {
                validator(_, value) {
                  if (value == null) {
                    return Promise.resolve()
                  }
                  if (selectedMinimum && compareDecimalAmounts(String(value), selectedMinimum) < 0) {
                    return Promise.reject(new Error(`最低結款金額為 ${formatMoney(selectedMinimum)} USD`))
                  }
                  if (compareDecimalAmounts(String(value), eligibility.availableBalance) > 0) {
                    return Promise.reject(new Error('結款金額超過可用餘額'))
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <InputNumber<string>
              stringMode
              step="0.01"
              precision={2}
              formatter={formatMoneyInput}
              parser={parseMoneyInput}
              style={{ width: '100%' }}
              addonBefore="$"
              addonAfter="USD"
              onChange={handleAmountChange}
            />
          </Form.Item>
          {method && selectedFeeConfig && (
            <Form.Item
              name="netAmount"
              label="到賬金額"
              dependencies={['amount', 'method']}
              rules={[
                { required: true, message: '請輸入到賬金額' },
                {
                  validator(_, value) {
                    if (value == null) return Promise.resolve()
                    if (method === 'PERSONAL_BANK' && !isPositiveMultipleOf(String(value), '500')) {
                      return Promise.reject(new Error('到賬金額須為 500.00 USD 的正整數倍'))
                    }
                    if (!selectedFeeConfig || calculatePayoutAmountFromNet(
                      String(value), selectedFeeConfig.feeRate, selectedFeeConfig.fixedFee,
                    ) == null) {
                      return Promise.reject(new Error('目前費率無法計算此到賬金額的結款金額'))
                    }
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <InputNumber<string>
                stringMode
                step={method === 'PERSONAL_BANK' ? '500' : '0.01'}
                precision={2}
                formatter={formatMoneyInput}
                parser={parseMoneyInput}
                style={{ width: '100%' }}
                addonBefore="$"
                addonAfter="USD"
                onChange={handleNetAmountChange}
              />
            </Form.Item>
          )}
          {feeEstimate && amount && (
            <div className="payout-fee-estimate" aria-live="polite">
              <Descriptions column={1} size="small" colon={false}>
                <Descriptions.Item label="預估手續費">
                  <Text strong className="payout-fee-estimate-value payout-fee-estimate-fee">
                    {formatMoney(feeEstimate.totalFee)} USD
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            </div>
          )}
          {eligibility.twoFactorEnabled && (
            <Form.Item
              name="totpCode"
              label="2FA 驗證碼"
              rules={[{ required: true, message: '請輸入 2FA 驗證碼' }]}
            >
              <Input maxLength={6} />
            </Form.Item>
          )}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              disabled={
                !eligibility.eligible ||
                !method ||
                !isCertificationApproved(method, eligibility) ||
                (method === 'USDT' && (payoutEwalletLoading || payoutEwalletLoadError || !payoutEwallet))
              }
              style={{ marginRight: 8 }}
            >
              提交結款申請
            </Button>
            <Button onClick={() => navigate('/dashboard/payouts')}>取消</Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  )
}
